import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MilestoneRow from "@/components/milestones/MilestoneRow";
import CounterMilestone from "@/components/milestones/CounterMilestone";
import PartnershipLaunch from "@/components/milestones/PartnershipLaunch";

const KIND_HELP = {
  achievement: "Completed achievements — tick once. Untick only to correct a mistake; the audit trail is kept.",
  qualification: "Ongoing qualifications — tick when qualified, untick when you're no longer qualified, tick again to requalify. Your first-achieved date is always kept.",
  counter: "A running total — enter how many you've completed, then use + and −.",
};
const TOAST = { check: "Recorded", uncheck: "Updated", correct: "Correction recorded", set_date: "Date saved", adjust: "Updated", set_count: "Total saved" };

function AnnualMilestone({ item, kind, records, data, busyKey, onAct, testid }) {
  const [year, setYear] = useState(String(data.current_year));
  const years = [...new Set([
    ...Array.from({ length: 12 }, (_, i) => String(data.current_year + 1 - i)),
    ...records.map((r) => r.period),
  ])].sort().reverse();
  const rec = records.find((r) => r.period === year);
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Qualification year</span>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="h-8 w-28" data-testid={`${testid}-year`}><SelectValue /></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
        </Select>
        {records.filter((r) => r.ever_achieved).map((r) => (
          <span key={r.period} className={`text-[10px] px-2 py-0.5 rounded-full border ${r.current ? "bg-[#C9A961]/20 border-[#C9A961] text-[#0A1F44]" : "border-slate-300 text-slate-500"}`}
                data-testid={`${testid}-chip-${r.period}`}>
            {r.period} · {r.current ? "qualified" : "ceased"}
          </span>
        ))}
      </div>
      <MilestoneRow item={{ ...item, title: `${item.title} — ${year}` }} kind={kind} record={rec} canEdit={data.can_edit}
                    busy={busyKey === `${item.id}|${year}`} today={data.today} testid={`${testid}-${year}`}
                    onAct={(b) => onAct(item.id, { ...b, period: year })} />
    </div>
  );
}

export default function BusinessMilestones({ userId }) {
  const [data, setData] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const load = useCallback(() => api.get(`/people/${userId}/milestones`).then((r) => setData(r.data))
    .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load milestones")), [userId]);
  useEffect(() => { load(); }, [load]);

  const act = async (mid, body) => {
    setBusyKey(`${mid}|${body.period || ""}`);
    try {
      await api.post(`/people/${userId}/milestones/${mid}`, body);
      await load();
      toast.success(TOAST[body.action]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally { setBusyKey(null); }
  };

  if (!data) return <div className="text-sm text-slate-400 py-6">Loading milestones…</div>;
  const recs = data.records;
  return (
    <div className="space-y-7" data-testid="business-milestones">
      {!data.can_edit && (
        <p className="text-xs text-slate-500" data-testid="milestones-readonly-note">View only — milestones can be updated by the partner or an Admin.</p>
      )}
      <PartnershipLaunch userId={userId} value={data.profile?.partnership_launch_date} canEdit={data.can_edit} today={data.today} onSaved={load} />
      {data.groups.map((g) => (
        <section key={g.id} data-testid={`milestone-group-${g.id}`}>
          <h3 className="font-serif text-lg text-[#0A1F44]">{g.title}</h3>
          <p className="text-[11px] text-slate-500 mb-3">{KIND_HELP[g.kind]}</p>
          <div className="space-y-2">
            {g.items.map((it) => g.kind === "counter" ? (
              <CounterMilestone key={it.id} item={it} record={recs.find((r) => r.milestone_id === it.id)} canEdit={data.can_edit}
                                busy={busyKey === `${it.id}|`} onAct={(b) => act(it.id, b)} testid={`milestone-${it.id}`} />
            ) : it.annual ? (
              <AnnualMilestone key={it.id} item={it} kind={g.kind} data={data} busyKey={busyKey} onAct={act}
                               records={recs.filter((r) => r.milestone_id === it.id)} testid={`milestone-${it.id}`} />
            ) : (
              <MilestoneRow key={it.id} item={it} kind={g.kind} record={recs.find((r) => r.milestone_id === it.id)}
                            hint={data.hints[it.id]} canEdit={data.can_edit} busy={busyKey === `${it.id}|`}
                            today={data.today} onAct={(b) => act(it.id, b)} testid={`milestone-${it.id}`} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
